/**
 * The Arche Store's pages in the app: each reads its data, sets its page head
 * (from the same SEO builders the snapshots use), and renders the
 * `@fias/public-site-ui` page body — adding only what the app knows: the live
 * search field, Add to Worktop and Share on an app's page, and states while
 * data is on its way.
 */
import { useEffect, useRef, useState, type ReactElement } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  STORE_SEARCH_PATH,
  StoreAppBody,
  StoreAppsBody,
  StoreCategoriesBody,
  StoreCategoryBody,
  StoreNotFoundBody,
  StoreSearchBody,
  StoreSearchForm,
  StoreStoryBody,
  StoreTodayBody,
  StoreTopBody,
  storeAppSeo,
  storeAppsSeo,
  storeCategoriesSeo,
  storeCategorySeo,
  storeStorySeo,
  storeTodaySeo,
  storeTopSeo,
  type PageSeo,
  type StoreCard,
} from '@fias/public-site-ui';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/auth/hooks/useAuth';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { siteOrigin, usePageHead } from '@/lib/document-head';
import { shareOrigin } from '@/lib/portal-host';
import { useFavoriteStatus } from '../../favorites/hooks/useFavoriteStatus';
import { STORE_CATEGORY_LABELS } from '../store-labels';
import {
  STORE_SEARCH_MIN_LENGTH,
  flattenAppsPages,
  useStoreApp,
  useStoreApps,
  useStoreCategories,
  useStoreHome,
  useStoreSearch,
  useStoreStory,
  useStoreSuggestions,
  useStoreTop,
} from '../hooks/useStoreCatalog';

const SEARCH_MAX = 100;

/** The page head from an SEO builder's answer (structured data only where the site origin is known). */
function useStoreHead(seo: PageSeo | null, noindex = false): void {
  usePageHead(
    seo
      ? {
          title: seo.title,
          description: seo.description,
          canonicalPath: seo.canonicalPath,
          jsonLd: siteOrigin() ? seo.jsonLd : [],
          noindex: noindex || undefined,
        }
      : null,
  );
}

const origin = () => siteOrigin() ?? '';

/** While a store page's data is on its way. */
export function StoreLoading(): ReactElement {
  return (
    <div className="fg-wrap fs-wrap" aria-busy="true">
      <div className="fg-empty" role="status">
        Loading the Arche Store…
      </div>
    </div>
  );
}

/** A store page whose data did not come: a way to try again. */
export function StoreLoadFailed({ onRetry }: { onRetry: () => void }): ReactElement {
  return (
    <div className="fg-wrap fs-wrap">
      <div className="fg-empty" role="alert">
        <p className="fg-empty__title">This didn’t load.</p>
        <p>Check your connection, then try again.</p>
        <p>
          <button type="button" className="fg-play fg-play--large" onClick={onRetry}>
            Try again
          </button>
        </p>
      </div>
    </div>
  );
}

/** Not on the store — kept out of search results. */
export function StoreNotFoundPage(): ReactElement {
  usePageHead({ title: 'Not found | Arche Store', noindex: true });
  return <StoreNotFoundBody />;
}

export function StoreTodayPage(): ReactElement {
  const home = useStoreHome();
  useStoreHead(home.data ? storeTodaySeo(home.data, origin()) : null);
  if (home.isError && !home.data) return <StoreLoadFailed onRetry={() => void home.refetch()} />;
  if (!home.data) return <StoreLoading />;
  return <StoreTodayBody data={home.data} />;
}

/** The Apps tab. Its search field takes the visitor to the search page. */
export function StoreAppsPage(): ReactElement {
  const home = useStoreHome();
  const navigate = useNavigate();
  useStoreHead(home.data ? storeAppsSeo(home.data, origin()) : null);
  if (home.isError && !home.data) return <StoreLoadFailed onRetry={() => void home.refetch()} />;
  if (!home.data) return <StoreLoading />;
  return (
    <StoreAppsBody
      data={home.data}
      search={
        <StoreSearchForm
          onSubmit={(value) => {
            const q = value.trim();
            navigate(q ? `${STORE_SEARCH_PATH}?q=${encodeURIComponent(q)}` : STORE_SEARCH_PATH);
          }}
        />
      }
    />
  );
}

export function StoreCategoriesPage(): ReactElement {
  const categories = useStoreCategories();
  useStoreHead(categories.data ? storeCategoriesSeo(categories.data.categories, origin()) : null);
  if (categories.isError && !categories.data) {
    return <StoreLoadFailed onRetry={() => void categories.refetch()} />;
  }
  if (!categories.data) return <StoreLoading />;
  return <StoreCategoriesBody categories={categories.data.categories} />;
}

const SORTS = ['popular', 'newest', 'name'] as const;
type Sort = (typeof SORTS)[number];

/** One category: `/a/store/category/<id>[?sort=newest|name]`. */
export function StoreCategoryPage(): ReactElement {
  const { categoryId = '' } = useParams();
  const [params] = useSearchParams();
  const sortParam = params.get('sort');
  const sort: Sort = (SORTS as readonly string[]).includes(sortParam ?? '')
    ? (sortParam as Sort)
    : 'popular';
  const known = Object.prototype.hasOwnProperty.call(STORE_CATEGORY_LABELS, categoryId);
  const apps = useStoreApps(known ? categoryId : '__unknown__', sort);
  const page = apps.data ? flattenAppsPages(apps.data.pages) : null;
  const category =
    page?.category ?? (known ? { id: categoryId, label: STORE_CATEGORY_LABELS[categoryId] } : null);
  useStoreHead(
    page && category ? storeCategorySeo({ ...page, category }, origin()) : null,
    // Only the popular order is the category's own page in search engines.
    sort !== 'popular',
  );
  if (!known) return <StoreNotFoundPage />;
  if (apps.isError && !page) return <StoreLoadFailed onRetry={() => void apps.refetch()} />;
  if (!page || !category) return <StoreLoading />;
  return (
    <StoreCategoryBody
      data={{ ...page, category }}
      sort={sort}
      onLoadMore={apps.hasNextPage ? () => void apps.fetchNextPage() : undefined}
      loadingMore={apps.isFetchingNextPage}
    />
  );
}

/** Top Charts: `/a/store/top[?category=<id>]`. */
export function StoreTopPage(): ReactElement {
  const [params] = useSearchParams();
  const raw = params.get('category');
  const category =
    raw && Object.prototype.hasOwnProperty.call(STORE_CATEGORY_LABELS, raw) ? raw : null;
  const top = useStoreTop(category);
  const categories = useStoreCategories();
  useStoreHead(top.data ? storeTopSeo(top.data, origin()) : null, category !== null);
  if (top.isError && !top.data) return <StoreLoadFailed onRetry={() => void top.refetch()} />;
  if (!top.data) return <StoreLoading />;
  return <StoreTopBody data={top.data} categories={categories.data?.categories ?? []} />;
}

export function StoreStoryPage(): ReactElement {
  const { slug = '' } = useParams();
  const story = useStoreStory(slug);
  useStoreHead(story.data ? storeStorySeo(story.data, origin()) : null);
  if (story.error instanceof ApiError && story.error.status === 404) return <StoreNotFoundPage />;
  if (story.isError && !story.data) return <StoreLoadFailed onRetry={() => void story.refetch()} />;
  if (!story.data) return <StoreLoading />;
  return <StoreStoryBody story={story.data} />;
}

/** Add to Worktop: the closest thing on Fias to having an app — nothing installs. */
function WorktopButton({ card }: { card: StoreCard }): ReactElement | null {
  const { isAuthenticated } = useAuth();
  const { isFavorite, isLoading, toggle } = useFavoriteStatus(card.archId);
  // A listing opens inside its arche: the Worktop keeps whole arches.
  if (card.kind === 'listing') return null;
  if (!isAuthenticated) {
    return (
      <Link to="/auth/login" className="fs-secondary">
        Sign in to add to your Worktop
      </Link>
    );
  }
  return (
    <button
      type="button"
      className="fs-secondary"
      aria-pressed={isFavorite}
      disabled={isLoading}
      onClick={() => toggle()}
    >
      {isFavorite ? 'On your Worktop' : 'Add to Worktop'}
    </button>
  );
}

/** Share: the device's share sheet where there is one, otherwise the link copied. */
function ShareButton({ title, path }: { title: string; path: string }): ReactElement {
  const [copied, setCopied] = useState(false);
  const share = async () => {
    const url = `${shareOrigin()}${path}`;
    try {
      if (navigator.share) {
        await navigator.share({ title, url });
        return;
      }
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } catch {
      // A dismissed share sheet is not an error worth showing.
    }
  };
  return (
    <button type="button" className="fs-secondary" onClick={() => void share()}>
      {copied ? 'Link copied' : 'Share'}
    </button>
  );
}

/** One app's page: `/a/store/app/<slug>`. */
export function StoreAppPage(): ReactElement {
  const { slug = '' } = useParams();
  const app = useStoreApp(slug);
  const { isAuthenticated } = useAuth();
  const notFound = app.error instanceof ApiError && app.error.status === 404;
  const seo = app.data ? storeAppSeo(app.data, origin(), STORE_CATEGORY_LABELS) : null;
  usePageHead(
    notFound
      ? { title: 'Not found | Arche Store', noindex: true }
      : seo
        ? {
            title: seo.title,
            description: seo.description,
            canonicalPath: seo.canonicalPath,
            jsonLd: siteOrigin() ? seo.jsonLd : [],
          }
        : null,
  );
  if (notFound) {
    return (
      <>
        <StoreNotFoundBody />
        {isAuthenticated && (
          <p className="fs-advanced fg-wrap">
            Shared with you, or not listed?{' '}
            <Link to={`/marketplace/arches/${encodeURIComponent(slug)}`}>
              Look for it in Advanced search
            </Link>
            .
          </p>
        )}
      </>
    );
  }
  if (app.isError && !app.data) return <StoreLoadFailed onRetry={() => void app.refetch()} />;
  if (!app.data) return <StoreLoading />;
  return (
    <StoreAppBody
      data={app.data}
      actions={
        <span className="fs-actions">
          <WorktopButton card={app.data.card} />
          <ShareButton title={app.data.card.title} path={app.data.card.pagePath} />
        </span>
      }
    />
  );
}

const RESULTS_ID = 'fs-search-results';

/**
 * Search: typing searches as you go, keeping `?q=` in the address (so a search
 * can be shared or reloaded); a `?q=` the page is opened with runs at once.
 */
export function StoreSearchPage(): ReactElement {
  const [params, setParams] = useSearchParams();
  const urlQuery = (params.get('q') ?? '').slice(0, SEARCH_MAX);
  const [text, setText] = useState(urlQuery);
  const debounced = useDebouncedValue(text, 250);
  const lastUrlQuery = useRef(urlQuery);

  useEffect(() => {
    const q = debounced.trim();
    if (q === urlQuery.trim()) return;
    lastUrlQuery.current = q;
    setParams(q ? { q } : {}, { replace: true });
    // Only the typed query drives this; the address is read, not watched.
  }, [debounced]);

  useEffect(() => {
    if (urlQuery !== lastUrlQuery.current) {
      lastUrlQuery.current = urlQuery;
      setText(urlQuery);
    }
  }, [urlQuery]);

  const q = urlQuery.trim();
  const searching = q.length >= STORE_SEARCH_MIN_LENGTH;
  const search = useStoreSearch(searching ? q : '');
  const suggestions = useStoreSuggestions();
  const categories = useStoreCategories();
  usePageHead({ title: q ? `“${q}” | Arche Store` : 'Search | Arche Store', noindex: true });

  const pending = searching && (!search.data || search.isPlaceholderData);
  const cards = searching ? (search.data?.cards ?? null) : null;
  const status = !searching
    ? ''
    : pending
      ? 'Searching…'
      : `${cards?.length ?? 0} result${cards?.length === 1 ? '' : 's'}`;

  return (
    <div id={RESULTS_ID}>
      <StoreSearchBody
        query={searching ? q : ''}
        cards={pending ? null : cards}
        suggestions={suggestions.data?.suggestions ?? []}
        categories={categories.data?.categories ?? []}
        status={status}
        search={
          <StoreSearchForm
            value={text}
            onChange={setText}
            onSubmit={(value) => {
              setText(value);
              const next = value.trim();
              lastUrlQuery.current = next;
              setParams(next ? { q: next } : {}, { replace: false });
            }}
            controlsId={RESULTS_ID}
          />
        }
      />
      {search.isError && <StoreLoadFailed onRetry={() => void search.refetch()} />}
    </div>
  );
}
